import { __assign, __spreadArray } from "tslib";
import React, { useEffect, useMemo, createContext, useCallback, useContext, } from 'react';
import { ScriptTagContext } from './ScriptTagContext';
import { reduce, stringify } from '../utils/polyfills';
function getIsObject(variable) {
    return (typeof variable === 'object' &&
        !Array.isArray(variable) &&
        variable !== null);
}
function getStorage(storageKey) {
    var _a;
    try {
        var data = JSON.parse(((_a = window === null || window === void 0 ? void 0 : window.localStorage) === null || _a === void 0 ? void 0 : _a.getItem(storageKey)) || '{}');
        var isObject = getIsObject(data);
        return isObject ? data : {};
    }
    catch (e) {
        return {};
    }
}
function setStorage(storageKey, data) {
    window.localStorage.setItem(storageKey, stringify(data));
}
function getItem(storageKey, key, defaultData) {
    var _a;
    try {
        var data = getStorage(storageKey);
        var url = window.location.href;
        if (key === null) {
            return (data === null || data === void 0 ? void 0 : data[url]) || defaultData;
        }
        return ((_a = data === null || data === void 0 ? void 0 : data[url]) === null || _a === void 0 ? void 0 : _a[key]) || defaultData;
    }
    catch (e) {
        return defaultData;
    }
}
function setItem(storageKey, key, value) {
    var _a;
    var _b;
    var url = window.location.href;
    var data = getStorage(storageKey);
    if ((_b = data === null || data === void 0 ? void 0 : data[url]) === null || _b === void 0 ? void 0 : _b[key]) {
        data[url][key] = value;
    }
    else {
        data[url] = __assign(__assign({}, ((data === null || data === void 0 ? void 0 : data[url]) || {})), (_a = {}, _a[key] = value, _a));
    }
    window.localStorage.setItem(storageKey, stringify(data));
}
function syncKeyWithLegacyVersion(_a) {
    var _b = _a.legacyStorage, legacyStorage = _b === void 0 ? { storageKey: '', itemKey: null } : _b, _c = _a.currStorage, currStorage = _c === void 0 ? { storageKey: '', itemKey: '' } : _c;
    try {
        var legacyValue = getItem(legacyStorage.storageKey, legacyStorage.itemKey, null);
        var currentValue = getItem(currStorage.storageKey, currStorage.itemKey, null);
        if (!legacyValue || currentValue) {
            return;
        }
        setItem(currStorage.storageKey, currStorage.itemKey, legacyValue);
    }
    catch (e) {
        console.error(e);
    }
}
export var StorageContext = createContext({
    getVisitTriggers: function () { return []; },
    setVisitTriggers: function (_) { },
    cleanupVisitTriggers: function (_) { },
    getSessions: function () { return []; },
    addSession: function (_) { },
    getData: function () { return ({}); },
});
export function StorageProvider(_a) {
    var children = _a.children, localStorageKey = _a.localStorageKey, legacyLocalStorageKey = _a.legacyLocalStorageKey;
    var scriptData = useContext(ScriptTagContext);
    var storageKey = "".concat(localStorageKey, "_").concat(scriptData.clientId, "_CAMPAIGN_TRIGGERS");
    useEffect(function () {
        if (legacyLocalStorageKey) {
            var legacyStorageKey = "".concat(legacyLocalStorageKey, "_").concat(scriptData.clientId, "_CAMPAIGN_TRIGGERS");
            syncKeyWithLegacyVersion({
                legacyStorage: { storageKey: legacyStorageKey, itemKey: null },
                currStorage: { storageKey: storageKey, itemKey: 'visits' },
            });
        }
    }, []);
    var getVisitTriggers = useCallback(function () { return getItem(storageKey, 'visits', []); }, [storageKey]);
    var setVisitTriggers = useCallback(function (value) {
        setItem(storageKey, 'visits', value);
    }, [storageKey]);
    var getSessions = useCallback(function () { return getItem(storageKey, 'sessions', []); }, [storageKey]);
    var addSession = useCallback(function (value) {
        var sessions = getItem(storageKey, 'sessions', []);
        setItem(storageKey, 'sessions', __spreadArray(__spreadArray([], sessions, true), [value], false));
    }, [storageKey]);
    var getData = useCallback(function () { return getStorage(storageKey); }, [storageKey]);
    var cleanupVisitTriggers = useCallback(function (cdnCampaignIds) {
        var data = getStorage(storageKey);
        var callbackFn = function (accStorage, url) {
            var _a;
            var _b, _c;
            var campaigns = (_c = (_b = data[url]) === null || _b === void 0 ? void 0 : _b.visits) === null || _c === void 0 ? void 0 : _c.filter(function (_a) {
                var campaignId = _a.campaignId;
                var doesCampaignExistOnCDN = !!cdnCampaignIds.find(function (cdnCampaignId) { return cdnCampaignId === campaignId; });
                return doesCampaignExistOnCDN;
            });
            return __assign(__assign({}, accStorage), (_a = {}, _a[url] = __assign(__assign({}, data[url]), { visits: campaigns }), _a));
        };
        var newStorage = reduce(Object.keys(data), callbackFn, {});
        setStorage(storageKey, newStorage);
    }, [storageKey]);
    var context = useMemo(function () { return ({
        getVisitTriggers: getVisitTriggers,
        setVisitTriggers: setVisitTriggers,
        cleanupVisitTriggers: cleanupVisitTriggers,
        getSessions: getSessions,
        addSession: addSession,
        getData: getData,
    }); }, []);
    return (React.createElement(StorageContext.Provider, { value: context }, children));
}
